import { LayoutPanelTop, RotateCcw, Rows3, Sparkles } from 'lucide-react';
import type { ConfigFaixaDeHorario, GrupoHorario, QuadroDia } from '@miolos/core';
import {
  ACABAMENTOS_DA_CAIXA,
  CHAVE_DO_GRUPO_HORARIO,
  DESCRICAO_ACABAMENTO_DA_CAIXA,
  DESCRICAO_QUADRO,
  GRUPOS_HORARIO,
  GRUPO_QUADRO,
  HORAS_DO_DIA,
  ICONE_QUADRO,
  INTERVALOS_HORARIO,
  QUADROS_DIA,
  QUADROS_RECOMENDADOS,
  ROTULO_ACABAMENTO_DA_CAIXA,
  ROTULO_ESTILO,
  ROTULO_GRUPO_HORARIO,
  ROTULO_QUADRO,
  gradeHorariosDe,
  horasDaGrade,
  obterModelo,
  quadrosDaLista,
  quadrosLigados,
  quadrosSugeridos,
} from '@miolos/core';
import type { AcabamentoDaCaixa } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Switch } from '@/components/ui/switch';
import { useEditor } from '../EstadoEditor';
import { Grade, TituloPasso } from '../ui';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { GlifoMotivo } from '@/impressao/GlifoMotivo';

/**
 * Ajustes da grade de horários.
 *
 * É o único quadro que precisa de ajuste próprio: cada grupo de dias tem o seu
 * início e o seu fim, e o grupo marcado como livre troca a grade por linhas de
 * anotação.
 */
function AjustesDaGrade() {
  const { config, atualizar } = useEditor();
  const grade = gradeHorariosDe(config);

  const mudar = (grupo: GrupoHorario, parcial: Partial<ConfigFaixaDeHorario>) => {
    const chave = CHAVE_DO_GRUPO_HORARIO[grupo];
    atualizar({ gradeHorarios: { ...grade, [chave]: { ...grade[chave], ...parcial } } });
  };

  const seletorDeHora = (
    valor: string,
    rotulo: string,
    aoEscolher: (novo: string) => void,
  ) => (
    <Select value={valor} onValueChange={aoEscolher}>
      <SelectTrigger className="h-10 w-[7.5rem] sm:h-9" aria-label={rotulo}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {HORAS_DO_DIA.map((hora) => (
          <SelectItem key={hora} value={hora}>
            {hora}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <section className="space-y-3 rounded-lg border border-primary/40 bg-primary/[0.03] p-4">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">Horário de cada grupo de dias</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          A grade acompanha o dia da página: uma terça usa o horário de{' '}
          <strong className="text-foreground">segunda a sexta</strong>, o domingo usa o do{' '}
          <strong className="text-foreground">domingo</strong>. O grupo marcado como{' '}
          <strong className="text-foreground">dia livre</strong> sai só com linhas para escrever, sem coluna de
          horas.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Linhas da grade:</span>
        {INTERVALOS_HORARIO.map((minutos) => (
          <Button
            key={minutos}
            variant={grade.intervaloMinutos === minutos ? 'secundario' : 'contorno'}
            tamanho="pequeno"
            onClick={() => atualizar({ gradeHorarios: { ...grade, intervaloMinutos: minutos } })}
          >
            {minutos === 60 ? 'De hora em hora' : 'De meia em meia hora'}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {GRUPOS_HORARIO.map((grupo) => {
          const faixa = grade[CHAVE_DO_GRUPO_HORARIO[grupo]];
          const quantas = faixa.livre ? 0 : horasDaGrade(faixa, grade.intervaloMinutos).length;
          const nome = ROTULO_GRUPO_HORARIO[grupo];

          return (
            <div
              key={grupo}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2"
            >
              <span className="min-w-[8.5rem] text-xs font-medium">{nome}</span>

              {faixa.livre ? (
                <span className="text-xs text-muted-foreground">
                  Dia livre — a página sai com linhas para escrever.
                </span>
              ) : (
                <>
                  <span className="text-xs text-muted-foreground">das</span>
                  {seletorDeHora(faixa.inicio, `Início — ${nome}`, (novo) => mudar(grupo, { inicio: novo }))}
                  <span className="text-xs text-muted-foreground">às</span>
                  {seletorDeHora(faixa.fim, `Fim — ${nome}`, (novo) => mudar(grupo, { fim: novo }))}
                  <span className="text-xs text-muted-foreground">{quantas} linha(s)</span>
                </>
              )}

              <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={faixa.livre}
                  onCheckedChange={(livre) => mudar(grupo, { livre })}
                  aria-label={`${nome} livre`}
                />
                Dia livre
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Escolha dos quadros que aparecem em cada página de dia.
 *
 * A coluna lateral recebe os quadros de conteúdo (palavra, prioridades, metas,
 * hábitos...) e a faixa inferior recebe os mini quadros de acompanhamento
 * (água, humor, tempo, gastos, aniversariantes).
 */
export function PassoQuadros() {
  const { config, atualizar, documento, paleta, modeloInicial } = useEditor();

  const alternar = (quadro: QuadroDia) =>
    atualizar({ quadros: { ...config.quadros, [quadro]: !config.quadros[quadro] } });

  const laterais = QUADROS_DIA.filter((quadro) => GRUPO_QUADRO[quadro] === 'LATERAL');
  const faixa = QUADROS_DIA.filter((quadro) => GRUPO_QUADRO[quadro] === 'FAIXA');

  const ligadosLaterais = quadrosLigados(config.quadros, 'LATERAL');
  const ligadosFaixa = quadrosLigados(config.quadros, 'FAIXA');

  const sugestao = QUADROS_RECOMENDADOS[config.estilo];
  const jaNaSugestao =
    ligadosLaterais.length + ligadosFaixa.length === sugestao.length &&
    sugestao.every((quadro) => config.quadros[quadro]);

  /* O miolo pode ter vindo de um modelo pronto: aí a agenda dele tem sugestão. */
  const modeloAplicado = modeloInicial ? obterModelo(modeloInicial) : null;
  const jaNaSugestaoDoModelo = Boolean(
    modeloAplicado &&
      ligadosLaterais.length + ligadosFaixa.length === modeloAplicado.quadrosSugeridos.length &&
      modeloAplicado.quadrosSugeridos.every((quadro) => config.quadros[quadro]),
  );

  const renderGrupo = (titulo: string, descricao: string, icone: React.ReactNode, quadros: QuadroDia[]) => (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          {icone}
          {titulo}
          <Badge tom="principal">{quadros.filter((q) => config.quadros[q]).length} ligados</Badge>
        </h3>
        <p className="text-xs leading-relaxed text-muted-foreground">{descricao}</p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {quadros.map((quadro) => {
          const ativo = config.quadros[quadro];
          return (
            <button
              key={quadro}
              type="button"
              onClick={() => alternar(quadro)}
              aria-pressed={ativo}
              className={[
                'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                ativo ? 'border-primary bg-primary/[0.04]' : 'border-border bg-card hover:border-primary/40',
              ].join(' ')}
            >
              <span
                className={[
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold',
                  ativo ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                ].join(' ')}
              >
                {ativo ? '✓' : ''}
              </span>
              <span className="space-y-0.5">
                <span className="block text-sm font-medium leading-tight">{ROTULO_QUADRO[quadro]}</span>
                <span className="block text-xs leading-relaxed text-muted-foreground">
                  {DESCRICAO_QUADRO[quadro]}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="O que aparece em cada página do dia"
        descricao="Nada vem marcado: monte a página do dia você mesmo, ou marque a sugestão do estilo com um clique. Os quadros de conteúdo entram na coluna ao lado da escrita; os de acompanhamento vão para uma faixa no pé da página."
      />

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        {/*
         * A sugestão da agenda do modelo só aparece quando o miolo veio de um
         * modelo pronto — e é o que sobrou da curadoria dele: **nada vem
         * marcado**, mas o conjunto que combina com aquela agenda está a um
         * clique.
         */}
        {modeloAplicado ? (
          <Button
            variant={jaNaSugestaoDoModelo ? 'secundario' : 'contorno'}
            tamanho="pequeno"
            onClick={() => atualizar({ quadros: quadrosDaLista(modeloAplicado.quadrosSugeridos) })}
          >
            <LayoutPanelTop />
            Sugeridos para {modeloAplicado.nome}
          </Button>
        ) : null}
        <Button
          variant={jaNaSugestao ? 'secundario' : 'contorno'}
          tamanho="pequeno"
          onClick={() => atualizar({ quadros: quadrosSugeridos(config.estilo) })}
        >
          <Sparkles />
          {/*
           * “Sem estilo (livre)” vira “agenda livre”: o rótulo do estilo existe
           * para identificar o estilo, e no botão ele soaria como se faltasse
           * algo — “Sugeridos para sem estilo” (é o caso da maioria dos modelos).
           */}
          {config.estilo === 'SEM_ESTILO'
            ? 'Sugeridos para agenda livre'
            : `Sugeridos para ${ROTULO_ESTILO[config.estilo].toLowerCase()}`}
        </Button>
        <Button
          variant="fantasma"
          tamanho="pequeno"
          onClick={() => atualizar({ quadros: quadrosSugeridos('SEM_ESTILO') })}
        >
          <RotateCcw />
          Deixar enxuto
        </Button>
        {jaNaSugestaoDoModelo ? <Badge tom="sucesso">Usando a sugestão da agenda</Badge> : null}
        {jaNaSugestao ? <Badge tom="sucesso">Usando a sugestão do estilo</Badge> : null}
        <span className="ml-auto text-xs text-muted-foreground">
          {documento.resumo.quadrosDoDia.length} quadro(s) no total · área de escrita com{' '}
          {documento.resumo.alturaEscritaMm} mm
        </span>
      </div>

      {renderGrupo(
        'Coluna lateral',
        'Ficam ao lado da área de escrita, empilhados. Se não couberem todos, os últimos são omitidos — e o gerador avisa quais ficaram de fora.',
        <LayoutPanelTop className="size-4" />,
        laterais,
      )}

      {renderGrupo(
        'Faixa inferior',
        'Mini quadros de acompanhamento diário. Em A4 cabem 5, em A5 cabem 4 e em A6 cabem 3.',
        <Rows3 className="size-4" />,
        faixa,
      )}

      {config.quadros.GRADE_HORARIOS ? <AjustesDaGrade /> : null}

      {/*
       * Desenho no título de cada quadro.
       *
       * A amostra mostra os desenhos reais dos quadros que estão ligados, na cor do
       * tema: escolher "ícones" sem ver o que vem é adivinhação.
       */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <LinhaSwitch
          rotulo="Desenho ao lado do título de cada quadro"
          descricao="A gota na água, o cupcake nos aniversariantes, o coração na gratidão: tira a cara de formulário das caixas do dia. O desenho sai na cor do tema."
          checked={Boolean(config.iconeQuadros)}
          onCheckedChange={(valor) => atualizar({ iconeQuadros: valor })}
        />

        {config.iconeQuadros && quadrosLigados(config.quadros).length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {quadrosLigados(config.quadros).map((quadro) => (
              <li
                key={quadro}
                className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground"
              >
                <GlifoMotivo motivo={ICONE_QUADRO[quadro]} cor={paleta.destaque} tamanhoMm={3.4} />
                {config.rotulosQuadros[quadro]?.trim() || ROTULO_QUADRO[quadro]}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {/*
       * Acabamento do título das caixas.
       *
       * É o que tira a cara de formulário sem mexer no conteúdo: nenhuma das
       * opções ocupa espaço, então escolher aqui não muda a medida da página.
       */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold">Como o título das caixas aparece</h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Vale para os quadros com caixa. O estilo visual escolhe um destes por você — aqui você
            troca à vontade, e nada do que está dentro muda de tamanho.
          </p>
        </div>

        <Grade colunas={2}>
          {ACABAMENTOS_DA_CAIXA.map((acabamento: AcabamentoDaCaixa) => (
            <CartaoOpcao
              key={acabamento}
              selecionado={(config.acabamentoDaCaixa ?? 'SIMPLES') === acabamento}
              titulo={ROTULO_ACABAMENTO_DA_CAIXA[acabamento]}
              descricao={DESCRICAO_ACABAMENTO_DA_CAIXA[acabamento]}
              aoSelecionar={() => atualizar({ acabamentoDaCaixa: acabamento })}
              marca={<MiniCaixa acabamento={acabamento} />}
            />
          ))}
        </Grade>
      </section>

      {/* Renomear quadros: é o que transforma “Prioridades” em “Dever de casa” */}
      {quadrosLigados(config.quadros).length > 0 ? (
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold">Renomear os quadros</h3>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Deixe em branco para usar o título padrão. Útil para adequar a linguagem: “Prioridades” pode virar
              “Dever de casa” ou “Tarefas do casamento”.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {quadrosLigados(config.quadros).map((quadro) => (
              <Campo key={quadro} rotulo={ROTULO_QUADRO[quadro]}>
                <Input
                  value={config.rotulosQuadros[quadro] ?? ''}
                  placeholder={ROTULO_QUADRO[quadro]}
                  maxLength={40}
                  onChange={(evento) =>
                    atualizar({
                      rotulosQuadros: {
                        ...config.rotulosQuadros,
                        [quadro]: evento.target.value,
                      },
                    })
                  }
                />
              </Campo>
            ))}
          </div>

          <Campo
            rotulo="Título da área de escrita"
            dica="Deixe em branco para usar “Anotações” ou “Lista do dia”."
          >
            <Input
              value={config.tituloAnotacoes}
              placeholder="Ex.: Meu diário, Dever de casa, Notas e decisões"
              maxLength={40}
              onChange={(evento) => atualizar({ tituloAnotacoes: evento.target.value })}
            />
          </Campo>
        </section>
      ) : null}

      {documento.avisos.length > 0 ? (
        <ul className="space-y-2">
          {documento.avisos.map((aviso, i) => (
            <li
              key={i}
              className={[
                'flex gap-2 rounded-md border p-3 text-xs leading-relaxed',
                aviso.nivel === 'ATENCAO'
                  ? 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
                  : 'border-border bg-muted/60 text-muted-foreground',
              ].join(' ')}
            >
              {aviso.mensagem}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * A amostra do acabamento: uma caixinha de quadro em miniatura.
 *
 * Mostra o enfeite **como ele sai na página** — o mesmo lugar do título, a mesma
 * cor de destaque do tema —, porque escolher "faixa no topo" sem ver onde ela cai
 * é adivinhação.
 */
function MiniCaixa({ acabamento }: { acabamento: AcabamentoDaCaixa }) {
  return (
    <span className="relative block size-8 overflow-hidden rounded border border-border bg-card">
      {acabamento === 'TOPO' ? (
        <span className="absolute inset-x-0 top-0 h-[3px] bg-primary" />
      ) : null}
      <span className="absolute inset-x-1 top-[9px] flex h-[6px] items-center justify-center">
        {acabamento === 'FAIXA' ? (
          <span className="absolute inset-x-[-3px] inset-y-0 rounded-sm bg-primary/15" />
        ) : null}
        <span className="relative h-[2px] w-3 rounded-full bg-primary/70" />
      </span>
      {acabamento === 'FILETE' ? (
        <span className="absolute inset-x-1 top-[17px] h-[1px] bg-primary/80" />
      ) : null}
      <span className="absolute inset-x-1 bottom-1.5 h-[1px] bg-border" />
    </span>
  );
}
